import { createContext, type ReactNode } from 'react';

export interface DreamComponentsContextType {
  /**
   * Lets a host swap a text block's rendered copy for its own editable surface, keyed by the
   * block's `attrs.id`. Returning null — which is every node bar the one being edited, and every
   * node at all when no host supplies this — renders the copy normally.
   *
   * Studio uses it to mount a Tiptap editor in place. Inverted like this so the host owns the
   * editor and this package keeps its render path free of any editor dependency.
   */
  renderEditableText?: (nodeId: string) => ReactNode | null;
  /**
   * Lets a host own the mobile navbar's hamburger state, so it can open the menu from outside the
   * canvas and hear about a press. Without it the burger wires no handler and stays shut.
   */
  navbarBurger?: { isOpen: boolean; setIsOpen: (open: boolean) => void };
  /**
   * Set by the builders for everything on their canvas, so a block can offer an affordance that
   * belongs to editing rather than to the page: an empty socials block outlines itself as
   * something to fill in, where a published site renders nothing at all. The serializer's
   * `data-node-id` cannot stand in for this, since it addresses nodes on the public site too.
   * A host that renders the published page never sets it.
   */
  isEditorCanvas?: boolean;
  responsiveImagesEnabled?: boolean;
  responsiveImagesBreakpoints?: number[];
  /** Number of images to eagerly load with high fetchPriority. Remaining images get loading="lazy". */
  imagePriorityThreshold?: number;
  /** Optional separate count for high-priority images. When set, only eligible images (ImageElement, carousel primary) use this budget for fetchPriority="high". Non-eligible images (logos) only consume the eager budget. */
  imageHighPriorityCount?: number;
  publicationTimeZone?: string;
  externalEmbedEnabled?: boolean;
  /** Full base URL for external embeds (e.g., 'https://abc123.beehiivusercontent.com' or 'http://kiliman.localhiiv:3002') */
  externalEmbedUrl?: string;
  /** BCP-47 locale tag for number/date formatting in components that can't access i18next directly. */
  language?: string;
  /** AdSense publisher id (ca-pub-...) used as data-ad-client for ad slot blocks. Undefined when AdSense is off. */
  adsensePublisherId?: string;
  rawHtmlPage?: boolean;
}

export const DREAM_COMPONENTS_CONTEXT_DEFAULT_VALUES: DreamComponentsContextType = {
  responsiveImagesEnabled: false,
  responsiveImagesBreakpoints: [360, 430],
  publicationTimeZone: undefined,
  externalEmbedEnabled: false,
  externalEmbedUrl: undefined,
  language: 'en-US',
};

export const DreamComponentsContext = createContext<DreamComponentsContextType | undefined>(undefined);

export const ImageIndexContext = createContext<(() => number) | null>(null);

export const HighPriorityIndexContext = createContext<(() => number) | null>(null);
